/* eslint-disable react/no-children-prop, react/no-unescaped-entities */
"use client";

import { useId, useRef, useState } from "react";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "@/app/_components/security/TurnstileWidget";
import ContactAlert from "@/app/_components/alerts/ContactAlert";
import { FieldError } from "@/app/_components/ui/FieldError";
import {
  useRequestDemoForm,
  type RequestDemoFormValues,
} from "@/hooks/useRequestDemoForm";
import countries from "../../../../public/countries.json";
import states from "../../../../public/states.json";

export function RequestDemoForm() {
  const id = useId();
  const [showAlert, setShowAlert] = useState(false);
  const [alertStatus, setAlertStatus] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null);

  const sortedCountries = [...countries].sort((a, b) => {
    if (a.name === "United States") {
      return -1;
    }
    if (b.name === "United States") {
      return 1;
    }
    return a.name.localeCompare(b.name);
  });

  const { form } = useRequestDemoForm({
    onSubmit: requestDemo,
  });

  async function requestDemo(data: RequestDemoFormValues) {
    try {
      const response = await fetch("/api/requestDemo", {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.message || "Failed to request demo");
      }

      const result = await response.json();

      if (result.success) {
        setAlertStatus("Success");
        setShowAlert(true);
        setAlertMessage(result.message);
        turnstileRef.current?.reset();
        return result;
      } else {
        throw new Error(result.message || "Request demo failed");
      }
    } catch (error) {
      setAlertStatus("Failed");
      setShowAlert(true);
      setAlertMessage(
        "Our servers are temporarily unavailable. Please try again later or contact us via email or phone",
      );
      turnstileRef.current?.reset();

      return {
        success: false,
        message: error instanceof Error ? error.message : "An error occurred",
      };
    }
  }

  function closeAlert() {
    setShowAlert(false);
  }

  return (
    <>
      {showAlert && (
        <ContactAlert
          showAlert={showAlert}
          alertMessage={alertMessage}
          alertStatus={alertStatus}
          registeredURL={null}
          registeredSerial={null}
          closeAlert={closeAlert}
        >
          <p>{alertMessage}</p>
        </ContactAlert>
      )}
      <section className="demo-section">
        <div className="left">
          <h1>Request a Demo</h1>
          <p>
            Fill out the form to request a demo of our products. A member of our
            team will be in touch with you shortly to schedule a demo.
          </p>
        </div>
        <div className="right">
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit();
            }}
            className="demo-form"
          >
            <div className="form-heading">Company Information</div>
            <form.Field
              name="company"
              children={(field) => (
                <div className="input-group">
                  <label htmlFor={`${id}-${field.name}`}>Company Name:</label>
                  <input
                    type="text"
                    id={`${id}-${field.name}`}
                    name={field.name}
                    placeholder="Enter company name"
                    value={field.state.value}
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  />
                  {!field.state.meta.isValid && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </div>
              )}
            />
            <div className="two-column-form-group">
              <form.Field name="companyWebsiteUrl">
                {(field) => (
                  <div className="sh2z3zJM">
                    <label htmlFor={`${id}-${field.name}`}>
                      Company Website URL
                    </label>
                    <input
                      type="text"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      value={field.state.value}
                      placeholder="Company Website"
                      autoComplete="new-password"
                      onChange={(event) => field.handleChange(event.target.value)}
                    />
                  </div>
                )}
              </form.Field>
              <form.Field
                name="address"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>
                      Street Address:
                    </label>
                    <input
                      type="text"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter street address"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="address2"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>Suite:</label>
                    <input
                      type="text"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter suite"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
            </div>
            <div className="three-column-form-group">
              <form.Field
                name="city"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>City:</label>
                    <input
                      type="text"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter city"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="state"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>State:</label>
                    <select
                      id={`${id}-${field.name}`}
                      name={field.name}
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    >
                      <option value="">Select State</option>
                      {states.map((state, index) => (
                        <option value={state.abbreviation} key={index}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="zip"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>Zip:</label>
                    <input
                      type="text"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter zip code"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
            </div>
            <div className="two-column-form-group">
              <form.Field
                name="country"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>Country:</label>
                    <select
                      id={`${id}-${field.name}`}
                      name={field.name}
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    >
                      <option value="">Select Country</option>
                      {sortedCountries.map((country, index) => (
                        <option value={country.id} key={index}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="website"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>Website:</label>
                    <input
                      type="text"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter website URL"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
            </div>

            <div className="form-heading">Primary Contact Info</div>
            <div className="two-column-form-group">
              <form.Field
                name="fname"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>Full Name:</label>
                    <input
                      type="text"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter full name"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="title"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>Job Position:</label>
                    <input
                      type="text"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter job position"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="phone"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>
                      Office Phone Number:
                    </label>
                    <input
                      type="tel"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter office phone number"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="mphone"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>
                      Mobile Number:
                    </label>
                    <input
                      type="tel"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter mobile number"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="email"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>
                      Email Address:
                    </label>
                    <input
                      type="email"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter email address"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
              <form.Field
                name="numLocs"
                children={(field) => (
                  <div className="input-group">
                    <label htmlFor={`${id}-${field.name}`}>
                      Number of Locations:
                    </label>
                    <input
                      type="number"
                      id={`${id}-${field.name}`}
                      name={field.name}
                      placeholder="Enter number of locations"
                      value={field.state.value}
                      aria-invalid={!field.state.meta.isValid}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {!field.state.meta.isValid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </div>
                )}
              />
            </div>

            <form.Field
              name="comment"
              children={(field) => (
                <div className="input-group">
                  <label htmlFor={`${id}-${field.name}`}>Comments:</label>
                  <textarea
                    id={`${id}-${field.name}`}
                    name={field.name}
                    placeholder="Enter comments"
                    value={field.state.value}
                    aria-invalid={!field.state.meta.isValid}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                  ></textarea>
                  {!field.state.meta.isValid && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </div>
              )}
            />
            <div style={{ marginBlock: "1rem" }}>
              <form.Field name="turnstileToken">
                {(field) => (
                  <div className="input-group">
                    <FieldError errors={field.state.meta.errors} />
                    <TurnstileWidget
                      action="request-demo"
                      ref={turnstileRef}
                      onTokenChange={(token) => field.handleChange(token)}
                      onTokenClear={() => field.handleChange("")}
                    />
                  </div>
                )}
              </form.Field>
            </div>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <button type="submit" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              )}
            />
          </form>
        </div>
      </section>
    </>
  );
}
